/**
 * <summary>
 * UploadIcons page lets users view and update Headquarter product & treatment icons.
 * fetches data, handles form submission, and shows success or error toasts.
 * Developer: Sahil Bharti
 * </summary>
 */
import { observer } from "mobx-react-lite";
import { useEffect } from "react";
import toast from "react-hot-toast";
import { FormattedMessage } from "react-intl";
import { useStore } from "../../contexts/store-provider";
import { getHeadquarterIdFromLocalStorage } from "../../helpers/localstorage-helper";
import {
  IHeadquarterVM,
  IUpdateHeadquarterUploadIcons
} from "../../models/response/IHeadquarterResponse";
import FormLoader from "../../shared-components/FormLoader/FormLoader";
import LanguageSlider from "../../shared-components/language-slider/language-slider";
import AddEditUploadIconsForm from "./forms/add-edit-upload-icons-form";

const UploadIcons = () => {
  const { headquarterStore } = useStore();
  const {
    headquarterDetails,
    UpdateHeadquarterUploadIconsService,
    addUpdateHeadquarterUploadIconsState,
    getHeadquartertByIdState,
    GetHeadquarterIconsByIdService,
    resetAddUpdateHeadquarterUploadIconsState,
    disableAction,
    setDisableAction
  } = headquarterStore;

  const HeadquaterId = getHeadquarterIdFromLocalStorage();

  const isUpdateMode = headquarterDetails?.Id > 0;

  useEffect(() => {
    if (HeadquaterId > 0) {
      GetHeadquarterIconsByIdService(HeadquaterId);
    }
    return(()=>{
      setDisableAction(false);
    })
  }, [HeadquaterId]);

  useEffect(() => {
    if (addUpdateHeadquarterUploadIconsState.success) {
      const hadIconsBefore =
        !!headquarterDetails?.ProductIcon ||
        !!headquarterDetails?.TreatmentIcon;

      toast.success(
        <FormattedMessage
          id={hadIconsBefore ? "updated_success" : "added_success"}
        />
      );
      resetAddUpdateHeadquarterUploadIconsState();

      GetHeadquarterIconsByIdService(HeadquaterId);
    }
  }, [addUpdateHeadquarterUploadIconsState.success]);

  useEffect(() => {
    if (addUpdateHeadquarterUploadIconsState.error) {
      toast.error(
        <FormattedMessage id={addUpdateHeadquarterUploadIconsState.error} />
      );
      resetAddUpdateHeadquarterUploadIconsState();
    }
  }, [addUpdateHeadquarterUploadIconsState.error]);

  const submitHandler = (data: IHeadquarterVM) => {
    const updateData: IUpdateHeadquarterUploadIcons = {
      Id: data.Id,
      ProductIcon: data.ProductIcon,
      TreatmentIcon: data.TreatmentIcon,
    };
    UpdateHeadquarterUploadIconsService(updateData);
  };

  return (
    <>
      {(getHeadquartertByIdState.inProgress ||
        addUpdateHeadquarterUploadIconsState.inProgress) && (
        <FormLoader
          loading={
            getHeadquartertByIdState.inProgress ||
            addUpdateHeadquarterUploadIconsState.inProgress
          }
        />
      )}
      <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
        <div className="row align-items-center">
          <div className="col-6">
            <div className="page_title_wrap">
              <h1 className="page_title">
                <FormattedMessage id="upload_icons" />
              </h1>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <FormattedMessage id="various_settings" />
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  <FormattedMessage id="upload_icons" />
                </li>
              </ol>
            </div>
          </div>
          <div className="col-6">
            <ul className="add-list">
              <li>
                <LanguageSlider />
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="container-fluid mt-4 p-0">
        <div className="row align-items-center justify-content-center">
          <div className="col-md-7 col-lg-6 col-xl-4 col-xxl-3 col-12">
            <div className="card">
              <div className="card-header p-3">
                <h3 className="card-title mb-0">
                  <FormattedMessage id="upload_icons" />
                </h3>
              </div>
              <div className="card-body">
                {addUpdateHeadquarterUploadIconsState.inProgress && (
                  <FormLoader
                    loading={addUpdateHeadquarterUploadIconsState.inProgress}
                  />
                )}
                <AddEditUploadIconsForm
                  id={HeadquaterId}
                  initialValues={headquarterDetails}
                  submitHandler={submitHandler}
                  loading={addUpdateHeadquarterUploadIconsState.inProgress}
                  isUpdateMode={isUpdateMode}
                  disableAction={disableAction}
                  addUpdateHeadquarterUploadIconsState={
                    addUpdateHeadquarterUploadIconsState
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default observer(UploadIcons);
